/**
 * Stable hash of the on-page shape graph used by `BoardCanvas` to skip
 * redundant auto-preview uploads (FS-482).
 *
 * The hash must change whenever the rendered board would visibly differ —
 * geometry (position/size/rotation), z-order (parent + index), visual style
 * (opacity, locked), or editable content (text/richText). Pure camera
 * pans/zooms and non-visible store events (e.g. session/state scope) must
 * NOT change it, which is why the hash is built from on-page shape data
 * rather than the full tldraw snapshot.
 *
 * Returns `null` when at least one shape has unresolved bounds
 * (width/height `null`). The caller treats that as "board still loading"
 * and both skips the upload AND refuses to pin a sentinel hash — so the
 * next capture after layout settles is correctly identified as new.
 *
 * Implementation notes:
 * - Sorted by `id` so set-equivalence (different traversal order) hashes
 *   the same.
 * - Coordinates and dimensions are rounded to integers; sub-pixel drift
 *   from tldraw's internal math should not invalidate the cache.
 * - Rotation kept to 3 decimal places; opacity to 2 — enough resolution
 *   to detect a user-meaningful change, coarse enough to ignore numeric
 *   jitter.
 * - `documentId` is included so swapping which document a slot represents
 *   counts as a change even when geometry happens to match.
 * - `text` is whatever signature the caller chose to extract from the
 *   shape's content (e.g. plain text, JSON-stringified richText, or
 *   concatenated label fields). The helper just hashes it.
 */
export interface BoardShapeForHash {
  id: string
  type: string
  x: number
  y: number
  /** `null` signals bounds not yet resolved by the editor (mid-load). */
  width: number | null
  height: number | null
  rotation: number
  opacity?: number
  isLocked?: boolean
  /** tldraw parent id — page id or another shape id when grouped. */
  parentId?: string
  /** tldraw fractional z-index string. */
  index?: string
  /** Caller-supplied content signature (plain text, richText JSON, etc.). */
  text?: string
  documentId?: string | number | null
}

export function computeBoardContentHash(shapes: BoardShapeForHash[]): string | null {
  if (shapes.some((s) => s.width == null || s.height == null)) {
    return null
  }
  return [...shapes]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((s) => {
      const docId = s.documentId != null && s.documentId !== '' ? String(s.documentId) : ''
      return [
        s.id,
        s.type,
        Math.round(s.x),
        Math.round(s.y),
        Math.round(s.width as number),
        Math.round(s.height as number),
        Math.round((s.rotation ?? 0) * 1000) / 1000,
        Math.round((s.opacity ?? 1) * 100) / 100,
        s.isLocked ? 'L' : '',
        s.parentId ?? '',
        s.index ?? '',
        s.text ?? '',
        docId,
      ].join('|')
    })
    .join(';')
}
